import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  loadRecord,
  reset,
  setQueryParams,
  setPdfData,
  setOrientation,
  setExecuteDynamicFilters,
} from "./reportSlice";
import { ResultSetRenderer } from "../../features/reports/GeneratedBuilder";
import { ResultSet } from "@cubejs-client/core";
import { useReportQuery } from "../../hooks";
import { Button, Space, Radio, Typography } from "antd";
import { DatePicker } from "../../components/antOverrides";
import FilterInput from "../reports/QueryBuilder/FilterInput";
import { useCubeMeta } from "@cubejs-client/react";
import cubejs from "@cubejs-client/core";
import { useAuthToken } from "../../hooks";

import {
  PrinterOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { FormattedMessage } from "react-intl";
import {
  getItems,
  setHeader,
} from "../../components/print/printSections/reports";
import {
  exportReportToPdf,
  getExcelheaders,
  getExcelData,
} from "../../components/util/helpers";
import { useIntl } from "react-intl";
import { CSVLink } from "react-csv";
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const Report = (props) => {
  const dispatch = useDispatch();
  const activeReport = useSelector((state) => state.report.activeReport);
  const pdfData = useSelector((state) => state.report.pdfData);
  const orientation = useSelector((state) => state.report.orientation);
  const executeDynamicFilters = useSelector(
    (state) => state.report.executeDynamicFilters
  );
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  const intl = useIntl();
  const report = { ...activeReport };
  const query = useReportQuery(report.id, { params: {} });
  const [excelData, setExcelData] = useState([]);
  const [availableDimensions, setAvailableDimensions] = useState(null);

  const [excelheaders, setExcelheaders] = useState();

  if (query.resultSet) {
    query.resultSet = new ResultSet(query.resultSet);
  }

  const token = useAuthToken();
  const cubejsApi = cubejs(process.env.REACT_APP_CUBEJS_TOKEN, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    apiUrl: `/api/v1/analytics`,
  });

  const meta = useCubeMeta(
    {
      cubejsApi: token && cubejsApi,
    },
    "query"
  );
  const metaDimensions =
    meta?.response?.meta?.cubes.map((cube) => [...cube.dimensions]).flat(1) ||
    [];

  useEffect(() => {
    if (metaDimensions.length > 0 && availableDimensions === null) {
      setAvailableDimensions(metaDimensions);
    }
  }, [metaDimensions]);

  useEffect(() => {
    dispatch(loadRecord({ scopePrefix, id: report.id }));
    return function cleanup() {
      dispatch(reset());
    };
  }, [scopePrefix, dispatch, report.id]);

  useEffect(() => {
    if (query.loading === false && report.id && query.resultSet) {
      let columns = query.resultSet.tableColumns();
      const items = getItems(query.resultSet, columns, intl, report);
      dispatch(setPdfData({ record: items }));
      dispatch(setOrientation({ record: columns.length > 6 ? "l" : "p" }));
      const annotation = query.resultSet.annotation();
      if (items.length > 0) {
        const headers = getExcelheaders(items, annotation, intl, "analytics");
        const data = getExcelData(items, headers, report);
        setExcelheaders(headers);
        setExcelData(data);
      }
    }
  }, [query.loading, report.id, dispatch]);
  const onChange = (value, dateString) => {
    if (value && value[0] && value[1]) {
      const queryParams = {
        from_date: value[0].format("YYYY-MM-DD"),
        to_date: value[1].format("YYYY-MM-DD"),
      };
      dispatch(setQueryParams({ queryParams }));
    }
  };

  const onPrint = async (action) => {
    const headerRecord = {
      name: activeReport.name,
      support_totals: activeReport?.support_totals,
    };
    exportReportToPdf(
      activeReport.name,
      pdfData,
      setHeader,
      headerRecord,
      null,
      null,
      pdfData,
      "paging",
      40,
      intl,
      "analytics",
      {
        styles: { fontSize: 9 },
        margin: { top: 26 },
      },
      orientation,
      action
    );
  };

  const onOrientationChange = (e) => {
    dispatch(setOrientation({ record: e.target.value }));
  };

  useEffect(() => {
    let filters = [];
    if (
      activeReport.dynamic_filters &&
      activeReport.dynamic_filters.length > 0 &&
      availableDimensions
    ) {
      activeReport.dynamic_filters.forEach((filter) => {
        const dimension = availableDimensions.filter(
          (i) => i.name === filter.member
        )[0];
        const id = `${filter.member}${filter.operator}`;
        filters.push({ ...filter, dimension, id });
      });
    }
    dispatch(setExecuteDynamicFilters(filters));
  }, [activeReport.dynamic_filters, availableDimensions, dispatch]);

  return (
    <div
      style={{
        padding: "0 10px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section - Fixed */}
      <div style={{ flex: "0 0 auto", marginBottom: "10px" }}>
        {/* Title and Actions */}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Title>{activeReport.name}</Title>
          {activeReport.chartType === "table" ? (
            <Space>
              <Radio.Group onChange={onOrientationChange} value={orientation}>
                <Radio value={"p"}>
                  {intl.formatMessage({
                    id: "app.reports.portrait",
                  })}
                </Radio>
                <Radio value={"l"}>
                  {intl.formatMessage({
                    id: "app.reports.landscape",
                  })}{" "}
                </Radio>
              </Radio.Group>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => onPrint("print")}
              >
                {" "}
                {<FormattedMessage id="app.actions.print" />}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => onPrint("save")}
              >
                {" "}
                {<FormattedMessage id="app.actions.save" />}
              </Button>
              <CSVLink
                data={excelData}
                headers={excelheaders}
                filename={`${activeReport.name}.csv`}
              >
                <Button icon={<FileExcelOutlined />}>
                  {" "}
                  {<FormattedMessage id="app.actions.export_to_excel" />}
                </Button>
              </CSVLink>
            </Space>
          ) : null}
        </div>

        {/* Filters */}
        <Space
          style={{
            paddingBottom: "10px",
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          {activeReport.support_date_range ? (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Text type="secondary">
                {intl.formatMessage({
                  id: "app.reports.reportDates",
                })}
              </Text>
              <RangePicker
                onChange={onChange}
                inputReadOnly={true}
                format={"DD/MM/YYYY"}
                style={{ width: "300px" }}
              />
            </div>
          ) : (
            <div />
          )}
          <div>
            {executeDynamicFilters.map((m) => (
              <FilterInput member={m} key={m.member} type="dynamic" />
            ))}
          </div>
        </Space>
      </div>

      {/* ResultSet Section - Take remaining height without adding scroll */}
      <div style={{ flex: "1 1 0", height: "100%" }}>
        <ResultSetRenderer
          {...report}
          name={report.name}
          resultSet={query.resultSet}
        />
      </div>
    </div>
  );
};

export default Report;
